import { useGT } from 'gt-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { WarningIcon } from '@phosphor-icons/react';
import { TroubleType, type TroubleData } from '@erp/shared-types';
import { cn } from '@/lib/utils';

export function hasActiveTrouble(
  troubleData: TroubleData | null | undefined,
  type?: TroubleType | string,
): boolean {
  if (!troubleData) return false;
  return troubleData.some((e) => e.active && (!type || e.type === type));
}

export function hasActiveTroubleValue(
  troubleData: TroubleData | null | undefined,
  value: string,
): boolean {
  if (!troubleData) return false;
  return troubleData.some((e) => e.active && e.value === value);
}

export function getActiveTroubleEntries(
  troubleData: TroubleData | null | undefined,
) {
  if (!troubleData) return [];
  return troubleData.filter((e) => e.active);
}

const TROUBLE_STYLES: Record<
  string,
  { className: string; iconClassName: string }
> = {
  [TroubleType.ON_HOLD]: {
    className: 'text-amber-700 border-amber-300 bg-amber-50',
    iconClassName: 'text-amber-600',
  },
  [TroubleType.MISMATCH]: {
    className: 'text-orange-700 border-orange-300 bg-orange-50',
    iconClassName: 'text-orange-600',
  },
  [TroubleType.ERROR]: {
    className: 'text-red-700 border-red-300 bg-red-50',
    iconClassName: 'text-red-600',
  },
  [TroubleType.ORDER_NOT_READY]: {
    className: 'text-blue-700 border-blue-300 bg-blue-50',
    iconClassName: 'text-blue-600',
  },
  [TroubleType.EXPECTED_COST_MISSING]: {
    className: 'text-violet-700 border-violet-300 bg-violet-50',
    iconClassName: 'text-violet-600',
  },
};

function getEffectiveEntries(
  troubleData: TroubleData | null | undefined,
  legacyHoldPay?: boolean,
  legacyConsign?: boolean,
) {
  const entries = getActiveTroubleEntries(troubleData);
  if (legacyHoldPay && !entries.some((e) => e.value === 'HOLD_PAY')) {
    entries.push({
      active: true,
      type: TroubleType.ON_HOLD,
      source: 'famous',
      value: 'HOLD_PAY',
    });
  }
  if (legacyConsign && !entries.some((e) => e.value === 'CONSIGN')) {
    entries.push({
      active: true,
      type: TroubleType.ON_HOLD,
      source: 'famous',
      value: 'CONSIGN',
    });
  }
  return entries;
}

function getStyle(type: string) {
  return (
    TROUBLE_STYLES[type] ?? {
      className: 'text-gray-700 border-gray-300 bg-gray-50',
      iconClassName: 'text-gray-600',
    }
  );
}

interface TroubleDataBadgesProps {
  troubleData: TroubleData | null | undefined;
  legacyHoldPay?: boolean;
  legacyConsign?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}

export function TroubleDataBadges({
  troubleData,
  legacyHoldPay,
  legacyConsign,
  className,
  size = 'sm',
}: TroubleDataBadgesProps) {
  const gt = useGT();
  const entries = getEffectiveEntries(
    troubleData,
    legacyHoldPay,
    legacyConsign,
  );
  if (entries.length === 0) return null;

  const sizeClasses =
    size === 'sm' ? 'text-[10px] px-1 py-0 h-4' : 'text-xs px-1.5 py-0.5';

  return (
    <div className={cn('inline-flex items-center gap-0.5', className)}>
      {entries.map((entry, i) => {
        const style = getStyle(entry.type);

        return (
          <Tooltip key={`${entry.type}-${i}`}>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={cn(style.className, sizeClasses, 'shrink-0')}
              >
                {translateLabel(entry.type, gt)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              sideOffset={4}
              className="max-w-xs text-xs space-y-0.5"
            >
              <p className="font-medium">{translateLabel(entry.type, gt)}</p>
              {entry.value && (
                <p className="text-muted-foreground font-mono">{entry.value}</p>
              )}
              {entry.source && (
                <p className="text-muted-foreground">
                  {gt('Source')}: {entry.source}
                </p>
              )}
              {entry.comments?.map((c, ci) => (
                <p key={ci}>{c}</p>
              ))}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

interface TroubleDataIconProps {
  troubleData: TroubleData | null | undefined;
  className?: string;
}

export function TroubleDataIcon({
  troubleData,
  className,
}: TroubleDataIconProps) {
  const gt = useGT();
  const entries = getActiveTroubleEntries(troubleData);
  if (entries.length === 0) return null;

  const labels = entries.map((e) => translateLabel(e.type, gt)).join(', ');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center text-amber-600 shrink-0',
            className,
          )}
        >
          <WarningIcon className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="text-xs">{labels}</TooltipContent>
    </Tooltip>
  );
}

interface TroubleDataAlertProps {
  onHoldCount: number;
  className?: string;
}

export function TroubleDataAlert({
  onHoldCount,
  className,
}: TroubleDataAlertProps) {
  const gt = useGT();
  if (onHoldCount === 0) return null;

  return (
    <div
      className={cn(
        'rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2',
        className,
      )}
    >
      <WarningIcon className="h-4 w-4 shrink-0 mt-0.5" />
      <p>
        {gt('{count} matched line(s) are on hold.', {
          count: String(onHoldCount),
        })}
      </p>
    </div>
  );
}

function translateLabel(type: string, gt: ReturnType<typeof useGT>): string {
  switch (type) {
    case TroubleType.ON_HOLD:
      return gt('On Hold');
    case TroubleType.MISMATCH:
      return gt('Mismatch');
    case TroubleType.ERROR:
      return gt('Error');
    case TroubleType.ORDER_NOT_READY:
      return gt('Order Not Ready');
    case TroubleType.EXPECTED_COST_MISSING:
      return gt('Missing Expected Freight Cost');
    default:
      return type;
  }
}

export interface OnHoldSummary {
  onHoldCount: number;
}

export const EMPTY_ON_HOLD_SUMMARY: OnHoldSummary = {
  onHoldCount: 0,
};
